import { useCallback, useEffect, useMemo } from 'react';
import { usePageFilters } from '@/stores/pageFiltersStore';
import {
  DEFAULT_FILTER, normaliseFilter, previousPeriod, resolvePreset,
  type AnalyticsFilter,
} from '@/pages/app/SocialMediaManagement/utils/analyticsHelpers';

/**
 * The single source of date-range truth for every analytics page.
 *
 * Three things it owns, all of which are easy to get subtly wrong per-page:
 *
 *  1. A stored PRESET is re-resolved on mount, so "Last 28 days" stays rolling instead
 *     of freezing to the 28 days it was when the filter was saved.
 *  2. Dates are kept as ISO strings. pageFiltersStore persists through JSON.stringify,
 *     so a Date would come back as a string and crash the picker on the next reload.
 *  3. The filter object is memoised and its arrays sorted, so react-query's key is
 *     stable across renders and equivalent filters share a cache entry.
 */
export function useSocialMediaFilters(pageKey: string, overrides?: Partial<AnalyticsFilter>) {
  const defaults = useMemo(
    () => ({ ...DEFAULT_FILTER, ...overrides }),
    // Overrides are route-derived (e.g. a locked platform) and stable per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(overrides ?? {})],
  );

  const [stored, , setFilters, clearFilters] = usePageFilters<AnalyticsFilter>(pageKey, defaults);

  // Re-resolve a preset on mount so rolling windows stay rolling.
  useEffect(() => {
    if (stored.preset && stored.preset !== 'custom') {
      const resolved = resolvePreset(stored.preset, stored);
      if (resolved.from !== stored.from || resolved.to !== stored.to) {
        setFilters(resolved);
      }
    }
    // Mount only: re-running on every filter change would fight manual date edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filter = useMemo(() => normaliseFilter({ ...defaults, ...stored }), [defaults, stored]);

  // The equivalent window immediately before, for period-over-period deltas.
  const comparisonFilter = useMemo(() => previousPeriod(filter), [filter]);

  const setFilter = useCallback(
    (next: AnalyticsFilter) => setFilters(normaliseFilter(next)),
    [setFilters],
  );

  return { filter, comparisonFilter, setFilter, clearFilters };
}
